import {
  ComponentPropsWithoutRef,
  forwardRef,
  useState,
  MouseEvent,
  useRef,
  useEffect,
} from "react";
import { useLayoutPropsUtil } from "../../internal/hooks";
import { DataTrackingId, LayoutUtilProps } from "../../types";
import cx from "classnames";
import styles from "./Overflow.module.scss";
import {
  childrenToString,
  getKeyboardFocusableElements,
} from "../../internal/functions";
import { useTrackingId } from "../../hooks";

/**
 * Props for the OverflowText component
 * @extends ComponentPropsWithoutRef<"div">
 * @extends LayoutUtilProps
 */
export type OverflowTextProps = ComponentPropsWithoutRef<"div"> &
  LayoutUtilProps & {
    /**
     * Number of rows to display before truncation
     * @default 1
     */
    rows?: number | "unlimited";
  } & (
    | {
        /**
         * Whether the text is expandable
         * @default false
         */
        expandable?: false;
        /**
         * Text for expand button (not used when expandable is false)
         */
        expandText?: never;
        /**
         * Text for collapse button (not used when expandable is false)
         */
        collapseText?: never;
        /**
         * Callback for expand event (not used when expandable is false)
         */
        onExpand?: never;
        /**
         * Callback for collapse event (not used when expandable is false)
         */
        onCollapse?: never;
      }
    | {
        /**
         * Whether the text is expandable
         */
        expandable: true;
        /**
         * Text to display on the expand button
         * @default "Show more..."
         */
        expandText?: string;
        /**
         * Text to display on the collapse button
         * @default "Show less..."
         */
        collapseText?: string;
        /**
         * Callback fired when content is expanded
         */
        onExpand?: (e: MouseEvent<HTMLButtonElement>) => void;
        /**
         * Callback fired when content is collapsed
         */
        onCollapse?: (e: MouseEvent<HTMLButtonElement>) => void;
      }
  ) &
  DataTrackingId;

/**
 * OverflowText component for text content with line truncation and expansion.
 *
 * Features:
 * - Configurable line limit with row-based truncation
 * - Expandable content with custom expand/collapse text
 * - Automatic height calculation and overflow detection
 * - Keyboard navigation support for focusable elements
 * - Resize observer for dynamic content changes
 * - Accessibility features with proper focus management
 * - Support for unlimited rows or specific row limits
 * - Callback support for expand/collapse events
 * - Layout utility props support
 *
 * @example
 * <OverflowText rows={3} expandable>
 *   <Text>
 *     This is a long text that will be truncated to 3 rows
 *     and can be expanded to show the full content.
 *   </Text>
 * </OverflowText>
 */
export const OverflowText = forwardRef<HTMLDivElement, OverflowTextProps>(
  (props, ref) => {
    const data = {
      children: childrenToString(props.children),
      rows: props.rows,
      expandable: props.expandable,
      expandText: props.expandText,
      collapseText: props.collapseText,
    };

    const trackingId = useTrackingId({
      name: "OverflowText",
      data,
      hasOverride: !!props["data-tracking-id"],
    });
    const { rows = 1, ...otherProps } = props;
    const { layoutStyles, componentProps } = useLayoutPropsUtil(otherProps);
    const [expanded, setExpanded] = useState(false);
    const [heights, setHeights] = useState<{
      clientHeight: number;
      scrollHeight: number;
    }>({ clientHeight: 0, scrollHeight: 0 });
    const { className, style, children, ...rest } = componentProps;

    const contentRef = useRef<HTMLDivElement>(null);

    const styledCombined = {
      ...style,
      ...layoutStyles,
      "--overflow-text-row":
        expanded || rows === "unlimited" ? "initial" : rows,
    };

    useEffect(() => {
      if (contentRef.current) {
        const observer = new ResizeObserver(() => {
          if (!contentRef.current) return;
          setHeights({
            clientHeight: contentRef.current.clientHeight,
            scrollHeight: contentRef.current.scrollHeight,
          });
        });

        observer.observe(contentRef.current);

        // Cleanup function
        return () => {
          observer.disconnect();
        };
      }
    }, []);

    const inertOverflowFocusables = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((element) => {
        if (!element.isIntersecting) {
          element.target.setAttribute("disabled", "true");
        } else {
          element.target.removeAttribute("disabled");
        }
      });
    };

    useEffect(() => {
      if (!("expandable" in props)) return;
      const observer = new IntersectionObserver(inertOverflowFocusables);
      const focusable = getKeyboardFocusableElements(contentRef.current);
      focusable.forEach((element) => {
        observer.observe(element);
      });
      // This is added because we don't need to watch for all props
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.expandable]);

    const showExpand = expanded || heights.clientHeight < heights.scrollHeight;

    if ("expandable" in props && props.expandable === true) {
      const {
        expandable,
        expandText,
        collapseText,
        onExpand,
        onCollapse,
        ...expandableRest
      } = rest;
      const expandString = expandText ?? "Show more...";
      const collapseString = collapseText ?? "Show less...";

      const onExpandClickHandler = (e: MouseEvent<HTMLButtonElement>) => {
        setExpanded((prev) => !prev);
        if (expanded) {
          onCollapse?.(e);
          return;
        }
        onExpand?.(e);
      };

      const overflowTextClassNames = cx(
        [styles["overflow-text"]],
        [styles["expandable"]],
        className,
        {
          [styles["expanded"]]: expanded,
        },
      );
      return (
        <div
          ref={ref}
          className={overflowTextClassNames}
          style={styledCombined}
          data-anv="overflow-text"
          data-tracking-id={trackingId}
          {...expandableRest}
        >
          <div className={styles["content"]} ref={contentRef}>
            {children}
          </div>
          {showExpand && (
            <button
              className={styles["expand-button"]}
              onClick={onExpandClickHandler}
            >
              {expanded ? collapseString : expandString}
            </button>
          )}
        </div>
      );
    }

    const overflowTextClassNames = cx([styles["overflow-text"], className]);
    return (
      <div
        ref={ref}
        className={overflowTextClassNames}
        style={styledCombined}
        data-anv="overflow-text"
        data-tracking-id={trackingId}
        {...rest}
      >
        <div className={styles["content"]}>{children}</div>
      </div>
    );
  },
);

OverflowText.displayName = "OverflowText";
